<template>
  <div>
    <b-card>
      <b-card-header>
        <b-card-title class="mb-0">Training Overview</b-card-title>
      </b-card-header>

      <b-card-body>
        <!-- One row from xl up; wraps to 3-4 per row on smaller screens. -->
        <div class="filter-panel mb-2">
          <b-row class="filter-row">
            <b-col cols="12" sm="6" md="4" xl class="filter-col filter-col--type">
              <label class="filter-label">Training type</label>
              <v-select
                :options="trainingTypes"
                :clearable="false"
                :searchable="false"
                label="label"
                v-model="filters.trainingType"
                @input="loadOverview"
              />
            </b-col>
            <b-col cols="12" sm="6" md="4" xl="auto" class="filter-col filter-col--year">
              <label class="filter-label">Year</label>
              <b-form-input type="number" v-model.number="filters.year" @change="loadOverview" :min="2000" :max="2099" />
            </b-col>
            <b-col cols="12" sm="6" md="4" xl class="filter-col">
              <filter-select title="Companies" :options="companies" v-model="filters.company" select-all @input="loadOverview" />
            </b-col>
            <b-col cols="12" sm="6" md="4" xl class="filter-col">
              <filter-select title="Courses" :options="courses" v-model="filters.course" select-all @input="loadOverview" />
            </b-col>
            <b-col cols="12" sm="6" md="4" xl class="filter-col">
              <filter-select title="Sites" :options="filteredSites" v-model="filters.site" select-all @input="loadOverview" />
            </b-col>
            <b-col cols="12" sm="6" md="4" xl class="filter-col">
              <filter-select title="Roles" :options="roles" v-model="filters.role" select-all @input="loadOverview" />
            </b-col>
            <b-col cols="12" sm="6" md="4" xl class="filter-col">
              <filter-select title="Users" :options="filteredUsers" v-model="filters.user" select-all @input="loadOverview" />
            </b-col>
          </b-row>
        </div>

        <section
          v-for="block in blocks"
          :key="block.key"
          class="overview-block"
          :class="`overview-block--${block.key}`"
        >
          <div class="block-heading">
            <span class="block-heading__icon">
              <feather-icon :icon="block.icon" size="16" />
            </span>
            <h5 class="mb-0">{{ block.title }}</h5>
          </div>

          <b-row class="kpi-row">
            <b-col v-for="kpi in kpiTiles(block)" :key="kpi.label" cols="12" sm="6" md="4" lg class="mb-1">
              <kpi-tile v-bind="kpi" />
            </b-col>
          </b-row>

          <div class="top-course">
            <feather-icon icon="AwardIcon" size="18" class="top-course__icon" />
            <span class="top-course__label">Top course by attendance</span>
            <span class="top-course__title">{{ block.kpis.top_course_by_attendance || '–' }}</span>
          </div>
        </section>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import store from '@/store'
import reportStoreModule from './reportStoreModule'
import vSelect from "vue-select";
import KpiTile from './KpiTile.vue'
import FilterSelect from './FilterSelect.vue'

const MODULE = 'trainingReports'

export default {
    components: {
      vSelect,
      KpiTile,
      FilterSelect,
    },
  name: 'TrainingOverview',
  data() {
    return {
      // One KPI set per training stream. `total` is its own query, not the sum
      // of the other two - a person who did both would otherwise be counted
      // twice in Participants and Companies.
      segments: { cis: {}, elearning: {}, total: {} },
      trainingTypes: [
        { id: 'all', label: 'All' },
        { id: 'cis', label: 'CIS Training' },
        { id: 'elearning', label: 'E-Learning [CPD]' },
      ],
      companies: [],
      courses: [],
      sites: [],
      roles: [],
      users: [],
      filters: {
        year: new Date().getFullYear(),
        trainingType: { id: 'all', label: 'All' },
        company: [],
        course: [],
        site: [],
        role: [],
        user: [],
      },
    }
  },
  computed: {
    blocks() {
      const type = (this.filters.trainingType || {}).id || 'all'
      const all = [
        { key: 'cis', title: 'CIS Training', icon: 'UsersIcon' },
        { key: 'elearning', title: 'E-Learning [CPD]', icon: 'MonitorIcon' },
        { key: 'total', title: 'Total', icon: 'LayersIcon' },
      ]
      return all
        .filter(b => type === 'all' || b.key === type)
        .map(b => ({ ...b, kpis: this.segments[b.key] || {} }))
    },
    filteredSites() {
      const ids = (this.filters.company || []).map(c => c.id)
      if (!ids.length) return this.sites
      return this.sites.filter(s => ids.includes(s.company_id))
    },
    filteredUsers() {
      const ids = (this.filters.company || []).map(c => c.id)
      if (!ids.length) return this.users
      return this.users.filter(u => ids.includes(u.company_id))
    },
  },
  created() {
    // Report.vue owns the shared module's lifetime; this only covers being
    // mounted on its own.
    if (!store.hasModule(MODULE)) store.registerModule(MODULE, reportStoreModule)
    this.loadOptions().then(this.loadOverview)
  },
  methods: {
    kpiTiles(block) {
      const k = block.kpis
      return [
        // Courses and events differ when one course runs as several events,
        // so both are shown.
        { label: 'Total Courses', value: k.total_courses || 0, icon: 'BookOpenIcon' },
        { label: 'Total Events', value: k.total_events_this_year || 0, icon: 'CalendarIcon' },
        { label: 'Total Participants', value: k.total_participants || 0, icon: 'UserCheckIcon' },
        // Self-paced e-learning has no register to miss: every import writes
        // an accepted application and a present attendance row, so the rate
        // would read a meaningless 100%.
        block.key === 'elearning'
          ? { label: 'Average Attendance Rate', value: '–', icon: 'PercentIcon', hint: 'Not applicable to self-paced e-learning' }
          : { label: 'Average Attendance Rate', value: this.formatPercent(k.average_attendance_rate), icon: 'PercentIcon' },
        { label: 'Companies Participating', value: k.total_companies_participating || 0, icon: 'BriefcaseIcon' },
      ]
    },
    loadOptions() {
      return store.dispatch('trainingReports/getTrainingOptions').then(res => {
        this.companies = res.data.companies || []
        this.courses = res.data.courses || []
        this.sites = res.data.sites || []
        this.roles = res.data.roles || []
        this.users = res.data.users || []
      })
    },
    loadOverview() {
      const params = this.buildParams({ year: this.filters.year })
      store.dispatch('trainingReports/getTrainingOverview', params).then(res => {
        this.segments = {
          cis: res.data.cis || {},
          elearning: res.data.elearning || {},
          // Fall back to `kpis` for an API that predates the split.
          total: res.data.total || res.data.kpis || {},
        }
      }).catch(err => {
        this.$swal({ title: 'Error', text: 'Failed to load overview: ' + err, icon: 'error' })
      })
    },
    buildParams(extra = {}) {
      const company_id = (this.filters.company || []).map(c => c.id).join(',') || undefined
      const course_id = (this.filters.course || []).map(c => c.id).join(',') || undefined
      const site_id = (this.filters.site || []).map(s => s.id).join(',') || undefined
      const role = (this.filters.role || []).map(r => r.id).join(',') || undefined
      const user_id = (this.filters.user || []).map(u => u.id).join(',') || undefined
      const training_type = (this.filters.trainingType || {}).id || 'all'
      return { ...extra, company_id, course_id, site_id, role, user_id, training_type }
    },
    formatPercent(v) {
      if (v === null || v === undefined) return '0%'
      return `${Number(v).toFixed(2)}%`
    },
  },
}
</script>

<style scoped>
/* Each stream gets one accent colour (Vuexy palette), set once per block and
   picked up by its heading, tiles and top-course strip. */
.overview-block {
  --accent: #7367f0;
  --accent-soft: rgba(115, 103, 240, 0.12);
  border: 1px solid #ebe9f1;
  border-left: 4px solid var(--accent);
  border-radius: 0.5rem;
  padding: 1rem 1.25rem 1.25rem;
  margin-bottom: 1.5rem;
  background: #fff;
  box-shadow: 0 4px 18px rgba(34, 41, 47, 0.05);
}
.overview-block--elearning { --accent: #00a8c2; --accent-soft: rgba(0, 207, 232, 0.12); }
.overview-block--total { --accent: #28c76f; --accent-soft: rgba(40, 199, 111, 0.12); }

.block-heading {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1rem;
}
.block-heading h5 { font-weight: 600; color: #5e5873; }
.block-heading__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: var(--accent-soft);
  color: var(--accent);
}

.kpi-row { margin-bottom: -0.25rem; }

.top-course {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem 0.75rem;
  margin-top: 0.75rem;
  padding: 0.7rem 1rem;
  border-radius: 0.45rem;
  background: var(--accent-soft);
}
.top-course__icon { color: var(--accent); }
.top-course__label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6e6b7b;
}
.top-course__title { font-weight: 600; color: #5e5873; }

/* Vuexy's dark skin puts .dark-layout on <body>. */
.dark-layout .overview-block { background: #283046; border-color: #3b4253; border-left-color: var(--accent); }
.dark-layout .block-heading h5,
.dark-layout .top-course__title { color: #d0d2d6; }
.dark-layout .top-course__label { color: #b4b7bd; }
.filter-panel {
  background: #f8f8f8;
  border: 1px solid #ebe9f1;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem 0.25rem;
}
.filter-row { margin-left: -0.4rem; margin-right: -0.4rem; }
.filter-col { padding-left: 0.4rem; padding-right: 0.4rem; margin-bottom: 0.5rem; min-width: 0; }
@media (min-width: 1200px) {
  .filter-col--type { flex: 0 0 11rem; max-width: 11rem; }
  .filter-col--year { width: 6.5rem; }
}
.dark-layout .filter-panel { background: #283046; border-color: #3b4253; }
.filter-label { font-size: 0.75rem; font-weight: 600; text-transform: uppercase; color: #6e6b7b; margin-bottom: 0.25rem; display: block; }
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>

